import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Bowser from "bowser";
import { useMutation } from "@tanstack/react-query";
import { AvailabilityForm, DetailsForm, FinanceForm } from "@/components/consultationForm/parts";
import { appSettingsConfig } from "@/lib/constants";
import { SuccessModal, LoadingSpinner } from "@/components/ui";
import { validators } from "@/lib/security";

import {
	postZap,
	sendEmail,
	getCurrentTime,
	getUserIP,
	useSearchEngineDetector,
	addProcessedIP,
	isIPProcessed,
} from "@/lib/utils";
import {
	AvailabilityFormData,
	AvailabilityFormErrors,
} from "@/components/consultationForm/parts/availability";
import { PhoneValidationData } from "@/components/consultationForm/parts/phoneValidation";

export interface FormData {
	callTimeStart: AvailabilityFormData["callTimeStart"];
	callTimeEnd: AvailabilityFormData["callTimeEnd"];
	callDays: AvailabilityFormData["callDays"];
	siteTitle: string;
	loanType: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	phoneVerified: boolean;
	phoneStage: PhoneValidationData["phoneStage"];
	state: string;
	postcode: string;
	tc: boolean;
	message: string;
}

export interface FormErrors {
	callTimeStart: AvailabilityFormErrors["callTimeStart"];
	callTimeEnd: AvailabilityFormErrors["callTimeEnd"];
	callDays: AvailabilityFormErrors["callDays"];
	firstName: boolean;
	lastName: boolean;
	email: boolean;
	phone: boolean;
	phoneVerified: boolean;
	state: boolean;
	postcode: boolean;
	tc: boolean;
	message: boolean;
	loanType: boolean;
}

export const InitialFormState: FormData = {
	callTimeStart: "8:30 AM",
	callTimeEnd: "4:30 PM",
	callDays: "",
	siteTitle: "",
	loanType: "",
	firstName: "",
	lastName: "",
	email: "",
	phone: "",
	phoneVerified: false,
	phoneStage: "enterPhone",
	state: "",
	postcode: "",
	tc: false,
	message: "",
};

export const InitialFormErrors: FormErrors = {
	callTimeStart: false,
	callTimeEnd: false,
	callDays: false,
	firstName: false,
	lastName: false,
	email: false,
	phone: false,
	phoneVerified: false,
	state: false,
	postcode: false,
	tc: false,
	message: false,
	loanType: false,
};

export function Desktop({ rootElement }: { rootElement: HTMLElement }) {
	// Form Data
	const [formData, setFormData] = useState<FormData>(InitialFormState);
	const [formErrors, setFormErrors] = useState<FormErrors>(InitialFormErrors);
	// Source Details
	const sourceUrl = window.location.href;
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const [browserInfo, setBrowserInfo] = useState({});
	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const headingRef = useRef<HTMLHeadingElement | null>(null);
	const [openSuccessModal, setOpenSuccessModal] = useState(false); // Open Success Modal
	const [enabledTC, setEnabledTC] = useState(formData.tc); // tc

	// Track each mutation's success states
	const submissionStatusRef = useRef({ mail: false, zap: false });

	const resetForm = useCallback(() => {
		setFormData(InitialFormState);
		setFormErrors(InitialFormErrors);
		setEnabledTC(InitialFormState.tc);
		// Reset tracking for next submission
		submissionStatusRef.current = { mail: false, zap: false };
	}, []);

	const checkBothSucceeded = () => {
		if (submissionStatusRef.current.mail && submissionStatusRef.current.zap) {
			setOpenSuccessModal(true);
			resetForm();
		}
	};

	const validateForm = () => {
		const newErrors = {
			callTimeStart: !formData.callTimeStart,
			callTimeEnd: !formData.callTimeEnd,
			callDays: formData.callDays ? !formData.callDays : false,
			loanType: formData.loanType ? !formData.loanType : false,
			firstName: !validators.name(formData.firstName),
			lastName: !validators.name(formData.lastName),
			email: formData.email ? !validators.email(formData.email) : false,
			phone: !validators.phone(formData.phone),
			phoneVerified: !formData.phoneVerified,
			state: !validators.name(formData.state),
			postcode: !validators.postcode(formData.postcode),
			tc: !formData.tc,
			message: formData.message ? !validators.textarea(formData.message) : false,
		};

		setFormErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
		return newErrors;
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validationErrors = validateForm();

		const hasErrors = Object.values(validationErrors).some((value) => value === true);
		if (hasErrors) return;

		sendMail.mutate();
		sendZap.mutate();

		resetForm();
	};

	// Availability Form
	const availFormData = useMemo(
		() => ({
			callTimeStart: formData.callTimeStart,
			callTimeEnd: formData.callTimeEnd,
			callDays: formData.callDays,
		}),
		[formData.callTimeStart, formData.callTimeEnd, formData.callDays]
	);

	const availFormErrors = useMemo(
		() => ({
			callTimeStart: formErrors.callTimeStart,
			callTimeEnd: formErrors.callTimeEnd,
			callDays: formErrors.callDays,
		}),
		[formErrors.callTimeStart, formErrors.callTimeEnd, formErrors.callDays]
	);

	// Finance Form
	const financeFormData = useMemo(
		() => ({
			loanType: formData.loanType,
		}),
		[formData.loanType]
	);
	const financeFormErrors = useMemo(
		() => ({
			loanType: formErrors.loanType,
		}),
		[formErrors.loanType]
	);

	// Details Form
	const detailsFormData = useMemo(
		() => ({
			firstName: formData.firstName,
			lastName: formData.lastName,
			email: formData.email,
			phone: formData.phone,
			phoneVerified: formData.phoneVerified,
			phoneStage: formData.phoneStage,
			state: formData.state,
			postcode: formData.postcode,
			tc: formData.tc,
			message: formData.message,
		}),
		[
			formData.firstName,
			formData.lastName,
			formData.email,
			formData.phone,
			formData.phoneVerified,
			formData.phoneStage,
			formData.state,
			formData.postcode,
			formData.tc,
			formData.message,
		]
	);
	const detailsFormErrors = useMemo(
		() => ({
			firstName: formErrors.firstName,
			lastName: formErrors.lastName,
			email: formErrors.email,
			phone: formErrors.phone,
			phoneVerified: formErrors.phoneVerified,
			state: formErrors.state,
			postcode: formErrors.postcode,
			tc: formErrors.tc,
			message: formErrors.message,
		}),
		[
			formErrors.firstName,
			formErrors.lastName,
			formErrors.email,
			formErrors.phone,
			formErrors.phoneVerified,
			formErrors.state,
			formErrors.postcode,
			formErrors.tc,
			formErrors.message,
		]
	);

	useEffect(() => {
		const rootSiteTitle = rootElement.getAttribute("data-title") ?? "Jade Finance";
		setFormData((prevData) => ({ ...prevData, siteTitle: rootSiteTitle }));
	}, [rootElement, setFormData]);

	// Apply Color Theme Settings
	useEffect(() => {
		if (!rootElement) return;
		const siteTitle = rootElement?.getAttribute("data-title") ?? "Jade Finance";

		rootElement.style.setProperty(
			`--jF-theme-color`,
			rootElement.getAttribute(`themeColor`) ?? appSettingsConfig[siteTitle].themeColor
		);
		rootElement.style.setProperty(
			`--jF-theme-h-text`,
			rootElement.getAttribute(`themeHeaderTxt`) ??
				appSettingsConfig[siteTitle].themeHeaderTxt
		);
		rootElement.style.setProperty(
			`--jF-hover-color`,
			rootElement.getAttribute(`hoverColor`) ?? appSettingsConfig[siteTitle].hoverColor
		);
		rootElement.style.setProperty(
			`--jF-bullet-bg`,
			rootElement.getAttribute(`bulletBG`) ?? appSettingsConfig[siteTitle].bulletBG
		);
	}, [rootElement, appSettingsConfig]);

	// Source Details For Zapier: IP, VPN, Browser Info, Search Engine
	const fetchIPData = useCallback(async () => {
		try {
			const { userIP, usingVPN } = await getUserIP();
			setUserIP(userIP);
			setUsingVPN(usingVPN);
		} catch (error) {
			console.error("Failed to fetch user IP data:", error);
		}
	}, []);

	useEffect(() => {
		if (formData.phoneVerified) {
			fetchIPData();
		}
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
		setSearchEngine(detectedSearchEngine);
	}, [formData.phoneVerified]);

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (formData.phoneVerified && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [formData.phoneVerified, userIP]);

	// Detect when tab is reloaded or closed
	useEffect(() => {
		const handleBeforeUnload = (event: any) => {
			if (formData.phoneVerified && !isIPProcessed(userIP)) {
				sendPreZap.mutate();
				sendPreMail.mutate();
				addProcessedIP(userIP);
			}
			resetForm();
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		window.addEventListener("pagehide", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			window.removeEventListener("pagehide", handleBeforeUnload);
		};
	}, [formData.phoneVerified, userIP]);

	const sendPreZap = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Partial Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== ""
				)
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendPreMail = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Partial Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== ""
				)
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendZap = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== ""
				)
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {
			submissionStatusRef.current.zap = true;
			checkBothSucceeded();
		},
	});

	const sendMail = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== ""
				)
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {
			submissionStatusRef.current.mail = true;
			checkBothSucceeded();
		},
	});

	// Track if any mutation is loading
	const isSubmitting = sendMail.isPending || sendZap.isPending;

	return (
		<form onSubmit={handleSubmit} className="jF-flex jF-flex-col jF-space-y-4 jF-relative">
			{/* Loading Overlay */}
			{isSubmitting && (
				<div className="jF-absolute jF-inset-0 jF-bg-white jF-bg-opacity-75 jF-z-50 jF-flex jF-items-center jF-justify-center jF-rounded-lg">
					<div className="jF-flex jF-flex-col jF-items-center jF-gap-3">
						<LoadingSpinner />
						<p className="jF-text-gray-600 jF-font-medium">
							Submitting your consultation form . . .
						</p>
					</div>
				</div>
			)}

			<div className="jF-flex jF-flex-col jF-gap-4 sm:jF-flex-row">
				<div className="jF-flex-1 jF-px-6 jF-py-6 jF-border jF-rounded-lg jF-border-gray-300 jF-shadow-md jF-bg-white">
					<AvailabilityForm
						formData={availFormData}
						setFormData={setFormData}
						formErrors={availFormErrors}
						setFormErrors={setFormErrors}
					/>
				</div>
				<div className="jF-flex-1 jF-px-6 jF-py-6 jF-border jF-rounded-lg jF-border-gray-300 jF-shadow-md jF-bg-white">
					<FinanceForm
						formData={financeFormData}
						setFormData={setFormData}
						formErrors={financeFormErrors}
						setFormErrors={setFormErrors}
					/>
				</div>
			</div>

			<div className="jF-flex jF-flex-col jF-w-full jF-px-6 jF-py-6 jF-border jF-rounded-lg jF-border-gray-300 jF-shadow-md jF-bg-white">
				<DetailsForm
					formData={detailsFormData}
					setFormData={setFormData}
					formErrors={detailsFormErrors}
					setFormErrors={setFormErrors}
					enabledTCstatus={enabledTC}
					setEnabledTCstatus={setEnabledTC}
				/>
			</div>

			<SuccessModal
				isOpen={openSuccessModal}
				onClose={() => {
					setOpenSuccessModal(false);
				}}
				modalTitle=""
				title="Sucessfully Submitted"
				description="Thank you for your consultation request. We will get back to you shortly."
				rootElement={rootElement}
				headingRef={headingRef}
			/>
		</form>
	);
}
